/**
 * Validador específico para arquivos de bloqueios
 * Garante que:
 * - Colunas obrigatórias: ID, Data de Criação, Data de Conclusão
 * - Se existir quarta coluna, deve ser "Categoria"
 * - Se coluna Categoria existir, não pode ter valores vazios
 * - Se coluna Categoria não existir, valores ficam como "Padrão"
 */

import { REQUIRED_BLOQUEIOS_HEADERS } from '../../chartUtils';

/**
 * Valida headers específicos para bloqueios
 * @param {string[]} headers - Array de nomes das colunas do arquivo
 * @returns {object} { valid: boolean, errors: string[], warnings: string[] }
 */
export function validateBloqueiosHeaders(headers) {
  const validation = {
    valid: true,
    errors: [],
    warnings: []
  };

  // 1. Verificar colunas obrigatórias
  const missingRequired = REQUIRED_BLOQUEIOS_HEADERS.filter(h => !headers.includes(h));
  if (missingRequired.length > 0) {
    validation.valid = false;
    validation.errors.push(`Colunas obrigatórias faltando: ${missingRequired.join(', ')}`);
  }

  // 2. Verificar se existe quarta coluna e se é "Categoria"
  if (headers.length >= 4) {
    const fourthColumn = headers[3].trim();
    if (fourthColumn !== 'Categoria') {
      validation.valid = false;
      validation.errors.push(`A quarta coluna deve ser "Categoria", mas encontrou: "${fourthColumn}"`);
    }
  }

  // 3. Verificar se há colunas além das esperadas
  const expectedColumns = ['ID', 'Data de Criação', 'Data de Conclusão', 'Categoria'];
  const unexpectedColumns = headers.filter(h => !expectedColumns.includes(h.trim()));
  
  if (unexpectedColumns.length > 0) {
    validation.warnings.push(`Colunas não reconhecidas serão ignoradas: ${unexpectedColumns.join(', ')}`);
  }

  // 4. Verificar ordem das colunas obrigatórias
  const idIndex = headers.indexOf('ID');
  const criacaoIndex = headers.indexOf('Data de Criação');
  const conclusaoIndex = headers.indexOf('Data de Conclusão');

  if (idIndex !== -1 && criacaoIndex !== -1 && idIndex > criacaoIndex) {
    validation.warnings.push('Recomenda-se que "ID" venha antes de "Data de Criação"');
  }

  if (criacaoIndex !== -1 && conclusaoIndex !== -1 && criacaoIndex > conclusaoIndex) {
    validation.warnings.push('Recomenda-se que "Data de Criação" venha antes de "Data de Conclusão"');
  }

  return validation;
}

/**
 * Gera mensagem de erro formatada para bloqueios
 * @param {object} validation - Resultado da validação
 * @param {string[]} headers - Headers encontrados no arquivo
 * @returns {string} Mensagem de erro formatada
 */
export function generateBloqueiosHeaderError(validation, headers) {
  let errorMessage = '❌ Erro na estrutura do arquivo de bloqueios:\n\n';
  
  if (validation.errors.length > 0) {
    errorMessage += '🔍 Problemas encontrados:\n';
    validation.errors.forEach(error => {
      errorMessage += `• ${error}\n`;
    });
    errorMessage += '\n';
  }

  errorMessage += '📋 Estrutura esperada:\n';
  errorMessage += '• Coluna 1: ID (obrigatória)\n';
  errorMessage += '• Coluna 2: Data de Criação (obrigatória)\n';
  errorMessage += '• Coluna 3: Data de Conclusão (obrigatória)\n';
  errorMessage += '• Coluna 4: Categoria (opcional, mas se presente deve estar na 4ª posição e não pode ter valores vazios)\n\n';

  errorMessage += '📋 Colunas encontradas no arquivo:\n';
  headers.forEach((header, index) => {
    errorMessage += `• Coluna ${index + 1}: ${header}\n`;
  });

  if (validation.warnings.length > 0) {
    errorMessage += '\n💡 Avisos:\n';
    validation.warnings.forEach(warning => {
      errorMessage += `• ${warning}\n`;
    });
  }

  return errorMessage;
}

/**
 * Valida se as colunas obrigatórias estão presentes (função legada compatível)
 * @param {string[]} headers - Array de nomes das colunas
 * @returns {string[]} Array de colunas faltando (vazio se todas presentes)
 */
export function validateHeaders(headers) {
  const validation = validateBloqueiosHeaders(headers);
  if (validation.valid) {
    return [];
  }
  
  // Retornar apenas colunas obrigatórias faltando para compatibilidade
  const missingRequired = REQUIRED_BLOQUEIOS_HEADERS.filter(h => !headers.includes(h));
  return missingRequired;
}
